import { router } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { colors, spacing } from '@/theme/tokens';

/**
 * "Po co ta aplikacja?" — mock: dlaczego_ta_aplikacja. The pitch screen:
 * lost policies + leaking money -> this app is for you. Exists so a client
 * who got the app from an agent understands what it does before logging in.
 */
export default function WhyAppScreen() {
  const { t } = useTranslation();

  const FEATURES = [
    { icon: 'shield-checkmark-outline', bg: colors.primaryTile, fg: colors.navy, key: 'policies' },
    { icon: 'wallet-outline', bg: colors.primaryTile, fg: colors.navy, key: 'budget' },
    { icon: 'headset-outline', bg: colors.warningBg, fg: colors.warning, key: 'agent' },
  ] as const;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('whyApp.title')} />

        {/* Hook — the CUK-style "masz problem? ta aplikacja jest dla Ciebie" */}
        <View style={{ alignItems: 'center', gap: spacing.base }}>
          <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
            {t('whyApp.hookLine1')}
          </AppText>
          <AppText variant="bodyLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('whyApp.hookLine2')}
          </AppText>
          <AppText variant="headlineSm" style={{ textAlign: 'center' }}>
            {t('whyApp.hookLine3')}
          </AppText>
        </View>

        {FEATURES.map((f) => (
          <Card key={f.key} style={{ alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.lg }}>
            <IconTile icon={f.icon} bg={f.bg} fg={f.fg} size={56} />
            <AppText variant="headlineSm" style={{ textAlign: 'center' }}>
              {t(`whyApp.${f.key}Title`)}
            </AppText>
            <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
              {t(`whyApp.${f.key}Body`)}
            </AppText>
          </Card>
        ))}

        {/* Who we are + CTA back into the app */}
        <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
          <AppText variant="headlineSm" color={colors.primary}>
            {t('whyApp.whoTitle')}
          </AppText>
          <AppText variant="bodyLg">{t('whyApp.whoBody')}</AppText>
          <PrimaryButton label={t('whyApp.lookAround')} onPress={() => router.push('/')} />
        </Card>
      </ScrollView>
    </SafeAreaView>
  );
}
